import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import { adminTokens } from 'ui-component/admin/AdminPage';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const collapsed = matchUpMd && !customization.opened;
  const isSelected = customization.isOpen.findIndex((id) => id === item.id) > -1;

  const Icon = item.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size="1.3rem" />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: MENU_OPEN, id });
    if (matchesSM) dispatch({ type: SET_MENU, opened: false });
  };

  // active menu item on page load
  useEffect(() => {
    const currentUrl = `${document.location.pathname}${document.location.search}`;
    if (item.url === currentUrl) {
      dispatch({ type: MENU_OPEN, id: item.id });
      return;
    }
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: MENU_OPEN, id: item.id });
    }
    // eslint-disable-next-line
  }, [pathname, search]);

  const button = (
    <ListItemButton
      {...listItemProps}
      aria-label={item.title}
      disabled={item.disabled}
      sx={{
        borderRadius: collapsed ? '10px' : '6px',
        mb: collapsed ? 0.55 : 0.35,
        mx: collapsed ? 'auto' : 0,
        width: collapsed ? 48 : '100%',
        minWidth: collapsed ? 48 : 'auto',
        minHeight: collapsed ? 48 : 44,
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
        py: collapsed ? 0 : level > 1 ? 0.75 : 0.95,
        px: collapsed ? 0 : 1.25,
        pl: collapsed ? 0 : `${Math.max(level * 18, 12)}px`,
        color: isSelected ? adminTokens.accent : '#33423D',
        '&.Mui-selected': {
          color: adminTokens.accent,
          bgcolor: adminTokens.hover,
          fontWeight: 700,
          '&:hover': { bgcolor: '#DDEDE8' },
          '& .MuiListItemIcon-root': { color: adminTokens.accent }
        },
        '&:hover': { bgcolor: '#F7FAFC', color: adminTokens.accent },
        '& .MuiListItemIcon-root': { color: isSelected ? adminTokens.accent : '#44534E' }
      }}
      selected={isSelected}
      onClick={() => itemHandler(item.id)}
    >
      <ListItemIcon sx={{ my: 'auto', minWidth: collapsed ? 0 : !item?.icon ? 18 : 34, justifyContent: 'center' }}>{itemIcon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={
            <Typography sx={{ fontSize: 14, fontWeight: isSelected ? 700 : 550, lineHeight: 1.35 }} color="inherit">
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                {item.caption}
              </Typography>
            )
          }
        />
      )}
      {!collapsed && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );

  if (!collapsed) {
    return button;
  }

  return (
    <Tooltip title={item.title} placement="right" arrow enterDelay={250}>
      {button}
    </Tooltip>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
